// ============================================================
// ✅ ValidationModal.jsx - Approuver ou Refuser une demande
// ============================================================

import React, { useState } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { X, Check, AlertCircle, Calendar, User, Clock } from 'lucide-react';

export default function ValidationModal({ isOpen, onClose, onSuccess, demande, actionType }) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [commentaire, setCommentaire] = useState('');

  // ==========================================================
// 💾 SOUMISSION
// ==========================================================
const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/demandes/valider', {
        ID_Demande: demande.ID_DdeConges,
        ID_Validateur: user.id,
        Decision: actionType,
        Commentaire: commentaire || null
      });

      if (response.data.success) {
        onSuccess(); // Rafraîchit la liste
        onClose();   // Ferme la modale
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la validation');
    } finally {
      setLoading(false);
    }
};

  // ==========================================================
  // 🚫 MODALE FERMÉE
  // ==========================================================
  if (!isOpen || !demande) return null;

  const isApprobation = actionType === 'Approuve';

  // ==========================================================
  // 🎨 RENDU
  // ==========================================================
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto animate-slide-up">

        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-2xl border-b px-5 py-4 flex justify-between items-center z-10">
          <h3 className={`text-lg font-semibold ${isApprobation ? 'text-emerald-600' : 'text-red-600'}`}>
            {isApprobation ? '✅ Approuver' : '❌ Refuser'} la demande
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={22} /></button>
        </div>

        {/* Erreur */}
        {error && (
          <div className="px-5 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2">
            <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto p-1 text-red-400 hover:text-red-600"><X size={16} /></button>
          </div>
        )}

        {/* Contenu */}
        <div className="p-5">

          {/* Résumé demande */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <User size={18} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">{demande.EmployePrenom} {demande.EmployeNom}</p>
                <p className="text-[10px] text-gray-400">{demande.Matricule}</p>
              </div>
            </div>

            <p className="text-sm font-semibold text-gray-700 mb-2">{demande.TypeCongeLibelle}</p>

            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Calendar size={12} className="text-gray-400" />
                <p className="text-xs text-gray-500">{demande.DateDebutFormatee} → {demande.DateFinFormatee}</p>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={12} className="text-gray-400" />
                <p className="text-xs text-gray-500">{demande.TotalDays} jour(s) ouvré(s)</p>
              </div>
            </div>

            {demande.Commentaire && (
              <div className="mt-3 p-2 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-700 italic">"{demande.Commentaire}"</p>
              </div>
            )}
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commentaire {isApprobation ? '(optionnel)' : ''}
              </label>
              <textarea
                className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 resize-none"
                rows={3}
                placeholder={isApprobation ? 'Ex: Bon congé !' : 'Ex: Période chargée...'}
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
              />
            </div>

            {/* Message de confirmation */}
            <div className={`p-3 rounded-xl text-sm ${isApprobation ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              <p className="font-medium">
                {isApprobation ? '✅ Vous allez approuver cette demande.' : '⚠️ Vous allez refuser cette demande.'}
              </p>
              <p className="text-xs mt-1">
                {isApprobation
                  ? "La demande sera transmise au niveau suivant s'il existe."
                  : 'La demande sera définitivement refusée.'}
              </p>
            </div>

            {/* Boutons */}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition text-sm font-medium">
                Annuler
              </button>
              <button type="submit" disabled={loading}
                className={`flex-1 px-4 py-3 rounded-xl transition text-sm font-medium text-white active:scale-95 disabled:opacity-50 ${isApprobation ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}`}>
                <span className="flex items-center justify-center gap-2">
                  {isApprobation ? <Check size={16} /> : <X size={16} />}
                  {loading ? 'En cours...' : isApprobation ? 'Approuver' : 'Refuser'}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}